<template>
    <el-dialog v-model="visible" title="Uutta Vahdissa" width="600px">
        <div v-for="date in visibleDates" :key="date">
            <h2>{{ date }}</h2>
            <div v-for="note in notesByDate(date)" :key="note.id" class="card-margin-bottom">
                <h3>{{ note.title }}</h3>
                <el-space>
                    <el-tag :type="noteTypeInfo(note.type).color" effect="dark">{{ noteTypeInfo(note.type).name }}</el-tag>
                    <template v-if="note.tags.length > 0">
                        <el-tag v-for="tag in note.tags" :key="tag" type="info" effect="light">{{ tag }}</el-tag>
                    </template>
                </el-space>
                <p style="white-space: pre-line">{{ note.description }}</p>
                <p v-if="note.link">
                    <el-link type="primary" :href="note.link" target="_blank">Lue lisää</el-link>
                </p>
            </div>
        </div>
        <div v-if="hasMore" style="text-align: center; margin-top: 16px;">
            <el-button @click="showAll = true">Näytä lisää</el-button>
        </div>
    </el-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { NoteType } from '@/types/Note'
import { useNotes } from '@/methods/notesComposables'
import { ElMessage } from 'element-plus'

const INITIAL_COUNT = 5

const visible = defineModel<boolean>({ required: true })

const { notes, error } = useNotes()
const showAll = ref(false)

watch(visible, (val) => {
    if (!val) {
        showAll.value = false
    }
})

watch(error, (value) => {
    if (value) {
        ElMessage.error('Julkaisutietojen haku epäonnistui')
    }
})

const allDates = computed(() => {
    if (!notes.value) return []
    return [...new Set(notes.value.map(note => note.date).reverse())]
})

const visibleDates = computed(() => {
    if (showAll.value) return allDates.value
    return allDates.value.slice(0, INITIAL_COUNT)
})

const hasMore = computed(() => {
    return !showAll.value && allDates.value.length > INITIAL_COUNT
})

const notesByDate = (date: string) => {
    return notes.value?.filter(note => note.date === date) ?? []
}

const noteTypeInfo = (value: string) => {
    const noteType = NoteType[value as keyof typeof NoteType]

    switch (noteType) {
        case NoteType.ench:
            return { name: 'OMINAISUUS', color: 'success' as const }
        case NoteType.fix:
            return { name: 'KORJAUS', color: 'danger' as const }
        case NoteType.perf:
            return { name: 'SUORITUSKYKY', color: 'warning' as const }
        case NoteType.info:
            return { name: 'TIEDOTE', color: 'info' as const }
        default:
            return { name: 'TUNTEMATON', color: 'info' as const }
    }
}
</script>
